import React, { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import SplashScreen from "./screens/SplashScreen";
import Login from "./screens/Login";
import AssetsPreloader from "./AssetsPreLoader";
import LandScape from "./utils/landscape";
import OTPVerification from "./screens/OTPVerification";
import DesktopScreen from "./screens/DesktopScreen";
import MachineCodeScan from "./screens/MachineCodeScan";
import { RouteLoader } from "./screens/RouteLoader";
import Dashboard from "./screens/Dashboard";
import BiscuitScan from "./screens/BiscuitScan";
import Registeration from "./screens/Registeration";
import ShareVideo from "./screens/ShareVideo";
import WelcomeBack from "./screens/WelcomeBack";
import Reward from "./screens/Reward";
import Menu from "./screens/Menu";
import PageNotFound from "./screens/PageNotFound";

import { ProtectedRoute } from "./utils/protectRoute";
import TnCScreen from "./screens/TnCScreen";
import HowToPlay from "./screens/HowToPlay";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import ContactUs from "./screens/ContactUs";
import AdminLogin from "./screens/admin/AdminLogin";
import CdpAdmin from "./screens/admin/CdpAdmin";
import { AdminProtectedRoute } from "./utils/protectAdminRoute";
import { gaEvent, trackMetaPageView } from "./analytics";


// Improved device detection function
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  if (isMobileUserAgent) {
    return true;
  }
  return hasTouch && (isIOS || isAndroid);
};

// Enhanced orientation detection
const isLandscapeMode = () => {
  if (window.screen.orientation) {
    const orientation = window.screen.orientation;
    return (
      orientation.type.includes("landscape") ||
      orientation.angle === 90 ||
      orientation.angle === 270
    );
  }
  if (typeof window.orientation !== "undefined") {
    return Math.abs(window.orientation) === 90;
  }
  if (window.matchMedia) {
    return window.matchMedia("(orientation: landscape)").matches;
  }
  return window.innerWidth > window.innerHeight;
};

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    gaEvent('page_view', { page_path: location.pathname + location.search });
    trackMetaPageView();
  }, [location]);
  return null;
}

function App() {
  const { t, i18n } = useTranslation();
  const language = localStorage.getItem("appLanguage") || "en";
  const [isMobile, setIsMobile] = useState(true);
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const checkDevice = () => setIsMobile(isMobileDevice());
    const checkOrientation = () => setIsLandscape(isLandscapeMode());

    checkDevice();
    checkOrientation();

    const handleResize = () => {
      checkDevice();
      checkOrientation();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    if (window.screen.orientation) {
      window.screen.orientation.addEventListener("change", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener("change", handleResize);
      }
    };
  }, []);

  if (!isMobile) {
    // Admin routes must be accessible from desktop too
    const adminPath = window.location.pathname;
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/cdp" element={<AdminProtectedRoute><CdpAdmin /></AdminProtectedRoute>} />
          {!adminPath.startsWith('/admin') && <Route path="*" element={<DesktopScreen />} />}
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <>
      <AssetsPreloader />
      <BrowserRouter>
        {isLandscape && (
          <div className="scan-popup">
            <div className="popup-card">
              <img src="assets/images/star-element.webp" className="popup-star" alt="Sparkle Element" />

              <div className="popup-inner screen-rotate-content">
                <img className="screen-rotate-heading img-fit" src={"assets/images/note-with-dots.webp"} alt="Congrats" />
                <p className="popup-text">{t("common.rotate_message")}<b>{t("common.rotate_messageBoldPart")}</b></p>
                <img src="assets/images/screen-rotate.webp" className="screen-rotate-icon" alt="Screen Rotate Icon" />
              </div>

            </div>
          </div>
        )}
        <PageViewTracker />
        <Routes>
          {/* ── Public routes ── */}
          <Route path="/" element={<SplashScreen />} />
          <Route path="/register" element={<Registeration />} />
          <Route path="/registration" element={<Registeration />} />
          <Route path="/login" element={<Login />} />
          <Route path="/otp-verification" element={<OTPVerification />} />
          <Route path="/welcome-back" element={<WelcomeBack />} />
          <Route path="/welcome-screen" element={<WelcomeBack />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/term-and-condition" element={<TnCScreen />} />
    

          {/* ── Protected routes ── */}
          <Route path="/home" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/biscuit-scan" element={<ProtectedRoute><BiscuitScan /></ProtectedRoute>} />
          <Route path="/batch-scan" element={<ProtectedRoute><MachineCodeScan /></ProtectedRoute>} />
          <Route path="/share-video" element={<ProtectedRoute><ShareVideo /></ProtectedRoute>} />
          <Route path="/welcome-back" element={<ProtectedRoute><WelcomeBack /></ProtectedRoute>} />
          <Route path="/welcome-screen" element={<ProtectedRoute><WelcomeBack /></ProtectedRoute>} />
          <Route path="/rewards" element={<ProtectedRoute><Reward /></ProtectedRoute>} />
          <Route path="/menu" element={<ProtectedRoute><Menu /></ProtectedRoute>} />
          <Route path="/tnc" element={<ProtectedRoute><TnCScreen /></ProtectedRoute>} />
          <Route path="/how-to-play" element={<ProtectedRoute><HowToPlay /></ProtectedRoute>} />
          <Route path="/privacy-policy" element={<ProtectedRoute><PrivacyPolicy /></ProtectedRoute>} />
          
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default React.memo(App);
